import { Add } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import { commonContainerWrapper } from "../Common/styles/flex";
import AddGlobalNoteModal from "../ParticipantRecords/ParticipantNotes/AddGlobalNoteModal";

interface RecentNote {
  id: string;
  addedBy: string;
  date: string;
  content: string;
}

interface Props {
  canEdit?: boolean;
}

const MAX_CONTENT_LENGTH = 100;

const GlobalParticipantNotesCard = ({ canEdit = false }: Props) => {
  const { participantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<RecentNote[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [toggleLoader, setToggleLoader] = useState(false);

  useEffect(() => {
    const fetchNotes = async () => {
      try {
        setLoading(true);
        const res = await http.get(`/participant-notes/${participantId}`);
        const mapped: RecentNote[] = (res.data?.data ?? []).map((item: any) => ({
          id: item.id,
          addedBy: item.createdByUser
            ? `${item.createdByUser.firstName || ""} ${
                item.createdByUser.lastName || ""
              }`.trim()
            : "",
          date: item.createdAt
            ? DateTime.fromISO(item.createdAt).toFormat("MM/dd/yyyy")
            : "",
          content: item.content ?? "",
        }));
        setNotes(mapped);
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotes();
  }, [participantId, toggleLoader]);

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const recentNotes = notes.slice(0, 3);

  return (
    <Box sx={{ ...commonContainerWrapper, mt: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h2" color="text.primary">
          Recent Notes
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowModal(true)}
            >
              Add Note
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          p: 2,
          backgroundColor: "#fff",
          borderRadius: "10px",
          mt: 3,
          minHeight: 56,
        }}
      >
        {loading ? (
          <LinearProgress />
        ) : recentNotes.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ py: 1 }}>
            No notes yet
          </Typography>
        ) : (
          <Box>
            {recentNotes.map((note, index) => (
              <Box key={note.id}>
                <Box sx={{ py: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      color="text.primary"
                      fontWeight={600}
                    >
                      {note.addedBy || "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {note.date}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.primary">
                    {note.content.length > MAX_CONTENT_LENGTH
                      ? `${note.content.substring(0, MAX_CONTENT_LENGTH)}...`
                      : note.content}
                  </Typography>
                </Box>
                {index < recentNotes.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {notes.length > 3 && (
        <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="text" size="small">
            View all notes
          </Button>
        </Box>
      )}

      {showModal && (
        <AddGlobalNoteModal
          showModal={showModal}
          closeModal={handleCloseModal}
          refreshPage={refreshPage}
        />
      )}
    </Box>
  );
};

export default GlobalParticipantNotesCard;
